import { useEffect } from 'react';

import { faRotateRight } from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import { getRenderingEngine } from '@cornerstonejs/core';
import styled from '@emotion/styled';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import classNames from 'classnames';
import { useAtom, useAtomValue } from 'jotai';
import { useResizeDetector } from 'react-resize-detector';

import type { LatestImage } from '@/@types/latestImages';
import { useGetImageWithSegmentations } from '@/react-query/useSegmentations';
import { annotationStyles } from '@/theme/annotationStyles';

import { SegmentationsHatchPatterns } from '../../../../common-components/elements/icon/SegmentationsHatchPatterns';
import { renderingEngineAtom } from '../../../../utils/cornerstone/cornerstoneAtoms';
import { RENDERING_ENGINE } from '../../../../utils/cornerstone/initializeCornerstone';
import type { CornerstoneImage } from '../../../../utils/cornerstone/useCornerstone';
import { useCornerstone } from '../../../../utils/cornerstone/useCornerstone';
import { useDicomCanvas } from '../../../../utils/cornerstone/useDicomCanvas';
import { mixpanelEvents, useTrackEvent } from '../../../../utils/mixpanel';
import annotatorConstants from '../../annotator.constants';
import { viewerModeAtom } from '../../atoms';
import { toothPartsTogglesAtom } from '../../findingSidebar/atoms';
import {
  isHQEnabledAtom,
  showSegmentationAtom,
} from '../../multiFileViewer/imageToolbar/atoms';
import { useRotation } from '../../multiFileViewer/RotationContext';

const StyledCompareImage = styled.div`
  ${annotationStyles}
`;

interface CompareImageProps {
  activeImage?: LatestImage | null;
  section?: 'left' | 'right';
  index?: number;
  visitId?: string;
}

export const CompareImage = ({
  activeImage,
  section,
  visitId,
}: CompareImageProps) => {
  const isRenderingEngineInitialized = useAtomValue(renderingEngineAtom);
  const [viewerMode] = useAtom(viewerModeAtom);
  const [isHQEnabled] = useAtom(isHQEnabledAtom);
  const showSegmentation = useAtomValue(showSegmentationAtom);
  const showAnatomySegmentation = useAtomValue(
    toothPartsTogglesAtom,
  ).tooth_anatomy;
  const { rotateImage } = useRotation();
  const { trackEvent } = useTrackEvent();

  const renderingEngine =
    isRenderingEngineInitialized && getRenderingEngine(RENDERING_ENGINE);

  const [dicomCanvasRef, setDicomCanvasRef] = useDicomCanvas();
  const { isCornerstoneReady, loadImageFileIntoCanvas, imageLoadStatus } =
    useCornerstone({
      isThumbnail: false,
      dicomCanvasRef,
      activeImage: activeImage as unknown as CornerstoneImage,
      viewportId: `compare-main-${section}`,
      source: 'compare-view',
      side: section,
      visitId,
    });

  const handleResize = () => {
    if (dicomCanvasRef && renderingEngine) {
      renderingEngine.resize();
    }
  };

  const { ref: containerRef } = useResizeDetector({
    handleHeight: true,
    handleWidth: true,
    skipOnMount: true,
    refreshMode: undefined,
    onResize: handleResize,
  });

  useEffect(() => {
    if (isCornerstoneReady && activeImage && dicomCanvasRef) {
      loadImageFileIntoCanvas(isHQEnabled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeImage?.imageId,
    activeImage?.imageUpdatedAt,
    dicomCanvasRef,
    isCornerstoneReady,
    isHQEnabled,
  ]);

  const isImageWithSegmentations =
    useGetImageWithSegmentations(activeImage?.imageId, visitId) &&
    activeImage?.detectedImageType !== 'COLOR_IMAGE';

  const imageClasses = classNames('cornerstoneElement', {
    'patient-view': isImageWithSegmentations && showSegmentation,
    'tooth-parts': showSegmentation && showAnatomySegmentation,
    '!vai-cursor-default': viewerMode !== annotatorConstants.MODES.MAGNIFYING,
    '!vai-cursor-zoom-in': viewerMode === annotatorConstants.MODES.MAGNIFYING,
    '!vai-cursor-crosshair': viewerMode === annotatorConstants.MODES.DRAWING,
  });

  return (
    <StyledCompareImage
      className="vai-relative vai-w-full vai-h-full"
      ref={containerRef}
    >
      <SegmentationsHatchPatterns />
      {imageLoadStatus === 'loading' && (
        <div className="vai-absolute vai-inset-0 vai-flex vai-items-center vai-justify-center vai-z-10">
          <div className="vai-animate-spin vai-rounded-full vai-h-8 vai-w-8 vai-border-t-2 vai-border-b-2 vai-border-sapphire-600"></div>
        </div>
      )}
      {imageLoadStatus === 'error' && (
        <div className="vai-absolute vai-inset-0 vai-flex vai-items-center vai-justify-center vai-text-red-500">
          <Trans>Error loading image</Trans>
        </div>
      )}
      <button
        className="vai-absolute vai-bottom-8 vai-left-0 vai-right-0 vai-mx-auto vai-w-8 vai-h-8 vai-bg-black vai-text-white vai-opacity-60 vai-hover:vai-opacity-80 vai-border-none vai-rounded vai-flex vai-items-center vai-justify-center vai-cursor-pointer vai-z-[1000]"
        onClick={() => {
          rotateImage(activeImage);
          trackEvent(mixpanelEvents.rotateImageClick, {
            section,
            visitId,
            imageId: activeImage?.imageId,
            imageType: activeImage?.detectedImageType,
          });
        }}
      >
        <FontAwesomeIcon icon={faRotateRight} size="sm" />
      </button>
      <div
        ref={setDicomCanvasRef}
        className={imageClasses}
        style={{ width: '100%', height: '100%' }}
      />
    </StyledCompareImage>
  );
};
